type ConsentState = 'granted' | 'denied' | 'pending'

type DataLayerEntry = unknown[] | Record<string, unknown>
type ConsentParams = { analytics_storage?: 'granted' | 'denied' }

declare global {
  interface Window {
    google_tag_data?: {
      ics?: {
        entries?: {
          analytics_storage?: {
            update?: boolean
          }
        }
      }
    }
    dataLayer?: DataLayerEntry[]
  }
}

// How long a CMP that is present but has not answered yet gets before the state is read again.
const PENDING_RECHECK_MS = 500

const consentEntryState = (entry: DataLayerEntry): 'granted' | 'denied' | undefined => {
  if (!Array.isArray(entry) || entry.length < 3) return undefined
  const [cmd, type, params] = entry as [unknown, unknown, ConsentParams]
  if (cmd !== 'consent') return undefined
  if (type !== 'default' && type !== 'update') return undefined
  const state = params?.analytics_storage
  return state === 'granted' || state === 'denied' ? state : undefined
}

const hasConsentEntries = (dataLayer: DataLayerEntry[]): boolean =>
  dataLayer.some(entry => Array.isArray(entry) && entry.length >= 3 && entry[0] === 'consent')

const scanDataLayerEntries = (dataLayer: DataLayerEntry[]): ConsentState => {
  for (let i = dataLayer.length - 1; i >= 0; i--) {
    const state = consentEntryState(dataLayer[i])
    if (state) return state
  }
  return 'pending'
}

const detectGoogleConsentMode = (): ConsentState => {
  // Sync path (Dreamdata pattern): GTM has already resolved consent
  if (window.google_tag_data?.ics?.entries?.analytics_storage?.update === true) {
    return 'granted'
  }

  const dataLayer = window.dataLayer
  if (!Array.isArray(dataLayer)) return 'pending'
  if (!hasConsentEntries(dataLayer)) return 'pending'

  return scanDataLayerEntries(dataLayer)
}

// navigator.globalPrivacyControl is a non-standard API not yet in TypeScript's lib.dom.d.ts
const hasGlobalPrivacyControl = (): boolean =>
  Boolean((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl)

const installDataLayerListener = (onState: (state: 'granted' | 'denied') => void): void => {
  const dataLayer = window.dataLayer
  if (!dataLayer) return

  const originalPush = dataLayer.push.bind(dataLayer)

  dataLayer.push = (...args: DataLayerEntry[]): number => {
    const result = originalPush(...args)

    for (const entry of args) {
      const state = consentEntryState(entry)
      if (state) {
        onState(state)
        break
      }
    }

    return result
  }
}

/**
 * The one consent policy both scripts apply: Global Privacy Control is a denial; Google Consent
 * Mode's analytics_storage is read from the dataLayer and followed on every later push; a CMP that
 * is present but silent is re-read once after 500 ms; no CMP means consent is not denied.
 *
 * `onChange` runs once with the initial state before this returns, then on every change.
 */
export const startConsentMonitor = (
  onChange: (denied: boolean) => void,
  log: (message: string) => void
): { isDenied: () => boolean } => {
  let denied = false

  const update = (nextDenied: boolean): void => {
    denied = nextDenied
    onChange(denied)
  }

  // GPC — legally binding opt-out in CA/CO
  if (hasGlobalPrivacyControl()) {
    log('GPC detected — consent denied')
    update(true)
    return { isDenied: () => denied }
  }

  // Ensure dataLayer exists so the listener can be installed regardless of CMP presence
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = []
  }

  const hasCmp = hasConsentEntries(window.dataLayer)
  const initialState = hasCmp ? detectGoogleConsentMode() : 'pending'

  if (hasCmp) {
    log(`Consent state: ${initialState}`)
  } else {
    log('No CMP detected — consent not denied')
  }

  update(initialState === 'denied')

  // Always install the persistent listener — catches consent changes at any time,
  // including from CMPs that push after our init (lazy banners, etc.).
  // Optimistic approach: track until explicitly denied.
  installDataLayerListener(state => update(state === 'denied'))

  if (initialState === 'pending' && hasCmp) {
    setTimeout(() => {
      if (detectGoogleConsentMode() === 'denied') {
        update(true)
      }
    }, PENDING_RECHECK_MS)
  }

  return { isDenied: () => denied }
}
